import React, { useState, useEffect, useRef } from 'react';
import { api, useServer } from '../contexts/ServerContext';
import toast from 'react-hot-toast';
import {
  Users,
  UserCheck,
  Terminal,
  Activity,
  Clock,
  AlertTriangle,
  Ban,
  Skull,
  MessageSquareOff,
  MicOff,
  Monitor,
  Gift,
  ArrowUpCircle,
  Smile,
  Trophy,
  Medal,
  Award
} from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import FogRawGLBackground from './FogRawGLBackground';
import { steamId64ToV2, getCachedAvatar } from '../utils/helpers';
import io from 'socket.io-client';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

function buildOnlineChart(data, liveOnline) {
  const now = Date.now();
  const buckets = [];
  for (let i = 23; i >= 0; i--) {
    const bucket = new Date(now - i * 60 * 60 * 1000);
    bucket.setMinutes(0, 0, 0);
    buckets.push(bucket);
  }
  const byBucket = new Map();
  (data || []).forEach(item => {
    const ts = new Date(item.timestamp).getTime();
    const bucket = new Date(ts);
    bucket.setMinutes(0, 0, 0);
    byBucket.set(bucket.getTime(), item.online);
  });
  const values = buckets.map((bucket, idx) => {
    if (typeof liveOnline === 'number' && idx === buckets.length - 1) {
      return liveOnline;
    }
    return byBucket.get(bucket.getTime()) ?? 0;
  });
  return {
    labels: buckets.map(b => b.getHours().toString().padStart(2, '0')),
    data: values,
    lastUpdate: new Date()
  };
}

function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function resolveProfileLinkId(steamid, sid64) {
  if (steamid && /^STEAM_0:/i.test(steamid)) return steamid;
  const id64 = sid64 || (/^\d{17}$/.test(String(steamid || '')) ? String(steamid) : '');
  if (id64) return steamId64ToV2(id64);
  return steamid || '';
}

function getDefaultAdminRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: toDateInputValue(from), to: toDateInputValue(to) };
}

function formatPeriodLabel(from, to) {
  if (!from || !to) return '';
  const fmt = (s) => {
    const [y, m, d] = s.split('-');
    return `${d}.${m}.${y}`;
  };
  return `${fmt(from)} — ${fmt(to)}`;
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [tab, setTab] = useState('players');
  const [online24h, setOnline24h] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [topAdmins, setTopAdmins] = useState([]);
  const defaultAdminRange = getDefaultAdminRange();
  const [adminDateFrom, setAdminDateFrom] = useState(defaultAdminRange.from);
  const [adminDateTo, setAdminDateTo] = useState(defaultAdminRange.to);
  const [adminRangeApplied, setAdminRangeApplied] = useState(defaultAdminRange);
  const [adminPeriodMeta, setAdminPeriodMeta] = useState(null);
  const [topAdminsLoading, setTopAdminsLoading] = useState(false);
  const [realtimeOnline, setRealtimeOnline] = useState([]);
  const { server } = useServer();
  
  // WebSocket и интервалы
  const socketRef = useRef();
  const intervalRef = useRef();
  const chartRef = useRef();

  useEffect(() => {
    if (!server) return;
    const timeout = setTimeout(() => {
      fetchDashboardData();
      fetchOnline24h();
      fetchDailyStats();
      initializeRealtimeOnline();
    });
    return () => clearTimeout(timeout);
  }, [activityPage, server]);

  useEffect(() => {
    if (!server) return;
    fetchTopAdmins(adminRangeApplied);
  }, [server, adminRangeApplied]);

  useEffect(() => {
    if (!server) return;
    const refreshStats = () => {
      api.get(`/api/admin/dashboard`, { withCredentials: true })
        .then(res => setStats(res.data))
        .catch(() => {});
    };
    refreshStats();
    const interval = setInterval(refreshStats, 30000);
    return () => clearInterval(interval);
  }, [server]);

  useEffect(() => {
    if (typeof stats?.onlinePlayers !== 'number') return;
    setRealtimeOnline(prev => {
      if (!prev?.data?.length) return prev;
      const newData = [...prev.data];
      newData[newData.length - 1] = stats.onlinePlayers;
      return { ...prev, data: newData, lastUpdate: new Date() };
    });
  }, [stats?.onlinePlayers]);

  // Инициализация WebSocket для реального времени
  useEffect(() => {
    if (!server) return;

    // Подключаемся к WebSocket
    socketRef.current = io('/', {
      transports: ['websocket'],
      path: '/ws'
    });

    // Подписываемся на обновления онлайна
    socketRef.current.emit('join_online_stats', { room: `online-${server}` });
    
    socketRef.current.on('online_update', (data) => {
      if (data.server === server) {
        updateRealtimeOnline(data.online, data.timestamp);
        setStats(prev => prev ? { ...prev, onlinePlayers: data.online } : prev);
      }
    });

    // Обработка ошибок подключения
    socketRef.current.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
    });

    // Запускаем интервал для обновления времени
    intervalRef.current = setInterval(() => {
      updateChartTime();
    }, 60000); // Каждую минуту

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_online_stats', { room: `online-${server}` });
        socketRef.current.disconnect();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [server]);

  const initializeRealtimeOnline = async () => {
    try {
      const [statsRes, onlineRes] = await Promise.all([
        api.get(`/api/admin/dashboard`, { withCredentials: true }),
        api.get(`/api/admin/server-online-stats?server=${server}`)
      ]);
      const data = onlineRes.data.data || [];
      setRealtimeOnline(buildOnlineChart(data, statsRes.data?.onlinePlayers));
    } catch (e) {
      console.error('Ошибка при инициализации реального времени:', e);
    }
  };

  const updateRealtimeOnline = (online, timestamp) => {
    setRealtimeOnline(prev => {
      if (!prev || !prev.labels || !prev.data) return prev;
      
      const now = new Date(timestamp || Date.now());
      const currentHour = now.getHours().toString().padStart(2, '0');
      const lastHour = prev.labels[prev.labels.length - 1];
      
      // Если это новый час, сдвигаем график
      if (currentHour !== lastHour) {
        const newLabels = [...prev.labels.slice(1), currentHour];
        const newData = [...prev.data.slice(1), online];
        
        return {
          labels: newLabels,
          data: newData,
          lastUpdate: now
        };
      } else {
        // Обновляем текущий час
        const newData = [...prev.data];
        newData[newData.length - 1] = online;
        
        return {
          ...prev,
          data: newData,
          lastUpdate: now
        };
      }
    });
  };

  // Функция для получения статуса подключения
  const getConnectionStatus = () => {
    if (!socketRef.current) return 'disconnected';
    if (socketRef.current.connected) return 'connected';
    return 'connecting';
  };

  const updateChartTime = () => {
    // Обновляем время на графике каждую минуту
    setRealtimeOnline(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        lastUpdate: new Date()
      };
    });
  };

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, activityResponse] = await Promise.all([
        api.get(`/api/admin/dashboard`, { withCredentials: true }),
        api.get(`/api/admin/activity?page=${activityPage}&limit=10`, { withCredentials: true })
      ]);

      setStats(statsResponse.data);
      setRecentActivity(activityResponse.data.activities || []);
      setActivityTotalPages(activityResponse.data.totalPages || 1);
    } catch (error) {
      toast.error('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  const fetchOnline24h = async () => {
    try {
      // Новый эндпоинт с params
      const res = await api.get(`/api/admin/server-online-stats?server=${server}`);
      //console.log('online24h debug:', res.data);
      setOnline24h(res.data.data || []);
    } catch (e) {
      setOnline24h(null);
    }
  };

  const fetchDailyStats = async () => {
    try {
      const res = await api.get(`/api/admin/stats/daily?server=${server}`);
      //console.log(res)
      setDailyStats(res.data);
    } catch (e) {
      setDailyStats(null);
    }
  };

  const fetchTopAdmins = async (range = adminRangeApplied) => {
    if (!range?.from || !range?.to) return;
    setTopAdminsLoading(true);
    try {
      const res = await api.get(`/api/admin/staff-weekly-stats`, {
        params: { dateFrom: range.from, dateTo: range.to, _t: Date.now() }
      });
      const periodTotal = res.data.periodTotalComplaints ?? (res.data.stats || []).reduce((sum, s) => sum + (s.complaintsCount || 0), 0);
      setAdminPeriodMeta({
        dateFrom: res.data.dateFrom,
        dateTo: res.data.dateTo,
        totalComplaints: periodTotal
      });
      const staffStats = (res.data.stats || []).filter(s => (s.complaintsCount || 0) > 0);

      const staffWithStats = await Promise.all(
        staffStats.map(async (s) => {
          const profileId = resolveProfileLinkId(s.steamid, s.sid64);
          let displayName = s.displayName;
          if (!displayName) {
            try {
              const playerRes = await api.get(`/api/players/${profileId}?server=${server}`);
              displayName = playerRes.data.displayName || playerRes.data.name;
            } catch {}
          }
          if (!displayName && s.sid64) {
            try {
              const resp = await api.get(`/api/steam/avatar/${s.sid64}`);
              displayName = resp.data.displayName || resp.data.personaname;
            } catch {}
          }
          let avatar = null;
          try {
            avatar = await getCachedAvatar(s.sid64, async () => {
              const resp = await api.get(`/api/steam/avatar/${s.sid64}`);
              return resp.data.avatar;
            });
          } catch {
            avatar = null;
          }
          return {
            ...s,
            profileId,
            displayName: displayName || 'Неизвестно',
            avatar,
            onlineHours: s.onlineHours || 0,
            complaintsCount: s.complaintsCount || 0
          };
        })
      );

      const activeAdmins = staffWithStats
        .sort((a, b) => b.complaintsCount - a.complaintsCount || b.onlineHours - a.onlineHours);

      setTopAdmins(activeAdmins);
    } catch (e) {
      const msg = e.response?.data?.error;
      if (msg) toast.error(msg);
      console.error('Ошибка при получении топ администрации:', e);
      setTopAdmins([]);
      setAdminPeriodMeta(null);
    } finally {
      setTopAdminsLoading(false);
    }
  };

  const applyAdminRange = () => {
    if (!adminDateFrom || !adminDateTo) {
      toast.error('Укажите обе даты');
      return;
    }
    if (adminDateFrom > adminDateTo) {
      toast.error('Дата начала не может быть позже даты окончания');
      return;
    }
    const range = { from: adminDateFrom, to: adminDateTo };
    setAdminRangeApplied(range);
    fetchTopAdmins(range);
  };

  function mapHourlyData(arr, key = 'count') {
    const map = {};
    arr.forEach(item => {
      const hour = item.hour.slice(-2);
      map[hour] = item[key];
    });
    return labels.map(l => map[l] ? Number(map[l]) : 0);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!online24h || !dailyStats) {
    return (
      <div className="flex justify-center items-center h-96">
        <span className="text-gmod-white text-lg">Загрузка статистики...</span>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Всего игроков',
      value: stats?.totalPlayers || 0,
      icon: Users,
      color: 'bg-theme-primary',
      textColor: 'text-theme-primary'
    },
    {
      title: 'Онлайн',
      value: stats?.onlinePlayers || 0,
      icon: UserCheck,
      color: 'bg-theme-primary-hover',
      textColor: 'text-theme-primary'
    },
    {
      title: 'Ожидающие команды',
      value: stats?.pendingCommands || 0,
      icon: Terminal,
      color: 'bg-gmod-lightest',
      textColor: 'text-gmod-white'
    },
    {
      title: 'Всего команд',
      value: stats?.totalCommands || 0,
      icon: Activity,
      color: 'bg-gmod-lighterer',
      textColor: 'text-gmod-white'
    }
  ];

  function getActionIcon(type) {
    switch (type) {
      case 'kick':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'ban':
        return <Ban className="h-4 w-4 text-red-500" />;
      case 'unban':
        return <Smile className="h-4 w-4 text-green-500" />;
      case 'slay':
        return <Skull className="h-4 w-4 text-orange-500" />;
      case 'mute':
        return <MessageSquareOff className="h-4 w-4 text-orange-500" />;
      case 'gag':
        return <MicOff className="h-4 w-4 text-orange-500" />;
      case 'screengrab':
        return <Monitor className="h-4 w-4 text-red-500" />;
      case 'givepifs':
        return <Gift className="h-4 w-4 text-pink-500" />;
      case 'setrankid':
        return <ArrowUpCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <Terminal className="h-4 w-4 text-blue-500" />;
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' });
  };

  // Создаем метки времени для всех графиков
  const labels = realtimeOnline && realtimeOnline.labels ? realtimeOnline.labels : Array.from({length: 24}, (_, i) => String(i).padStart(2, '0'));

  // Данные для графика онлайна (используем реальное время если доступно, иначе статические данные)
  const onlineData = realtimeOnline && realtimeOnline.labels ? {
    labels: realtimeOnline.labels,
    datasets: [
      {
        label: 'График онлайна',
        data: realtimeOnline.data,
        borderColor: '#e11d8e',
        backgroundColor: 'rgba(225,29,142,0.2)',
        fill: true,
        tension: 0.3,
      },
    ],
  } : {
    labels,
    datasets: [
      {
        label: 'График онлайна',
        data: buildOnlineChart(online24h, stats?.onlinePlayers).data,
        borderColor: '#e11d8e',
        backgroundColor: 'rgba(225,29,142,0.2)',
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const uniqueTypes = Array.from(new Set((dailyStats?.punishments || []).map(p => p.type)));
  const punishmentsData = {
    labels,
    datasets: uniqueTypes.map((type, idx) => ({
      label: type,
      data: mapHourlyData((dailyStats?.punishments || []).filter(p => p.type === type)),
      backgroundColor: [
        '#e11d8e', '#a21caf', '#2563eb', '#f59e42', '#22d3ee', '#fbbf24', '#6366f1', '#f472b6', '#f87171', '#10b981', '#f43f5e', '#facc15'
      ][idx % 12],
      stack: 'punishments',
    })),
  };
  const commandsData = {
    labels,
    datasets: [
      {
        label: 'Всего',
        data: mapHourlyData(dailyStats?.commands || [], 'total'),
        borderColor: '#22d3ee',
        backgroundColor: 'rgba(34,211,238,0.2)',
        fill: true,
        tension: 0.3,
      }
    ],
  };
  const pvpData = dailyStats?.pvp && dailyStats.pvp.length > 0 ? {
    labels,
    datasets: [
      {
        label: 'PVP-убийства',
        data: mapHourlyData(dailyStats.pvp),
        backgroundColor: '#a21caf',
      },
    ],
  } : null;

  function extractAdminSayMessage(command) {
    if (!command) return '';
    const first = command.indexOf('"');
    if (first === -1) return '';
    const second = command.indexOf('"', first + 1);
    if (second === -1) return '';
    return command.slice(second + 1).trim();
  }

  return (
    <>
      <FogRawGLBackground style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 0 }} />
      <div className="flex">
        <div className="flex-1">
          {tab === 'players' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat) => (
                  <div key={stat.title} className="bg-gmod-dark rounded-lg p-6 border border-gmod-lighterer">
                    <div className="flex items-center">
                      <div className={`p-3 rounded-lg ${stat.color}`}>
                        <stat.icon className="h-6 w-6 text-gmod-white" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gmod-white opacity-60">{stat.title}</p>
                        <p className="text-2xl font-bold text-gmod-white">{stat.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Графики за сутки */}
              {dailyStats && (
                <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer mt-8 p-6">
                  <h2 className="text-lg font-semibold text-gmod-white mb-4">Статистика за 24 часа</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    {/* Онлайн (24ч) */}
                    <div className="bg-gmod-dark rounded-lg p-4 border border-gmod-lighterer flex flex-col items-center h-96">
                      <h3 className="text-gmod-white text-lg font-semibold mb-2 flex items-center gap-2">
                        Онлайн (24ч) 
                      </h3>
                      <Line
                        data={onlineData}
                        options={{
                          plugins: { legend: { labels: { color: '#fff' } } },
                          scales: { x: { ticks: { color: '#fff' } }, y: { ticks: { color: '#fff' } } },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                        height={320}
                      />
                    </div>
                    {/* Наказания по типам */}
                    <div className="bg-gmod-dark rounded-lg p-4 border border-gmod-lighterer flex flex-col items-center h-96">
                      <h3 className="text-gmod-white text-lg font-semibold mb-2">Наказания</h3>
                      <Bar
                        data={punishmentsData}
                        options={{
                          plugins: { legend: { labels: { color: '#fff' } } },
                          scales: { x: { stacked: true, ticks: { color: '#fff' } }, y: { stacked: true, ticks: { color: '#fff' } } },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                        height={320}
                      />
                    </div>
                    {/* Команды */}
                    <div className="bg-gmod-dark rounded-lg p-4 border border-gmod-lighterer flex flex-col items-center h-96">
                      <h3 className="text-gmod-white text-lg font-semibold mb-2">Команды</h3>
                      <Line
                        data={commandsData}
                        options={{
                          plugins: { legend: { labels: { color: '#fff' } } },
                          scales: { x: { ticks: { color: '#fff' } }, y: { ticks: { color: '#fff' } } },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                        height={320}
                      />
                    </div>
                    {/* PVP */}
                    {pvpData && (
                      <div className="bg-gmod-dark rounded-lg p-4 border border-gmod-lighterer flex flex-col items-center h-96">
                        <h3 className="text-gmod-white text-lg font-semibold mb-2">PVP-убийства</h3>
                        <Bar
                          data={pvpData}
                          options={{
                            plugins: { legend: { labels: { color: '#fff' } } },
                            scales: { x: { ticks: { color: '#fff' } }, y: { ticks: { color: '#fff' } } },
                            responsive: true,
                            maintainAspectRatio: false,
                          }}
                          height={320}
                        />
                      </div>
                    )}
                    {/* Топ администрации */}
                    <div className="bg-gmod-dark rounded-lg p-4 border border-gmod-lighterer flex flex-col h-96">
                      <div className="w-full mb-3">
                        <h3 className="text-gmod-white text-lg font-semibold mb-2">Топ администрации</h3>
                        <div className="flex flex-wrap items-end gap-2">
                          <label className="flex flex-col gap-1">
                            <span className="text-gmod-white opacity-60 text-xs">С</span>
                            <input
                              type="date"
                              value={adminDateFrom}
                              max={adminDateTo || undefined}
                              onChange={(e) => setAdminDateFrom(e.target.value)}
                              className="bg-gmod-lighter text-gmod-white text-sm rounded px-2 py-1 border border-gmod-lighterer"
                            />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-gmod-white opacity-60 text-xs">По</span>
                            <input
                              type="date"
                              value={adminDateTo}
                              min={adminDateFrom || undefined}
                              max={toDateInputValue(new Date())}
                              onChange={(e) => setAdminDateTo(e.target.value)}
                              className="bg-gmod-lighter text-gmod-white text-sm rounded px-2 py-1 border border-gmod-lighterer"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={applyAdminRange}
                            className="px-3 py-1 bg-theme-primary text-gmod-white text-sm rounded hover:opacity-90"
                          >
                            Показать
                          </button>
                        </div>
                        <p className="text-gmod-white opacity-50 text-xs mt-2">
                          {formatPeriodLabel(adminRangeApplied.from, adminRangeApplied.to)}
                          {adminPeriodMeta ? ` · репортов за период: ${adminPeriodMeta.totalComplaints}` : ''}
                        </p>
                      </div>
                      <div className="w-full flex-1 overflow-y-auto min-h-0">
                        {topAdminsLoading ? (
                          <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
                          </div>
                        ) : topAdmins.length === 0 ? (
                          <div className="flex items-center justify-center h-full px-4 text-center">
                            <p className="text-gmod-white opacity-60">
                              {adminPeriodMeta?.totalComplaints === 0
                                ? 'За этот период обработанных репортов нет. Попробуйте более поздние даты.'
                                : 'Нет данных за выбранный период'}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3 w-full">
                            {topAdmins.map((admin, index) => {
                              const getPrizeIcon = (place) => {
                                switch (place) {
                                  case 0: return <Trophy className="h-5 w-5 text-yellow-500" />;
                                  case 1: return <Medal className="h-5 w-5 text-gray-400" />;
                                  case 2: return <Award className="h-5 w-5 text-amber-600" />;
                                  default: return <span className="text-gmod-white font-bold text-lg">{place + 1}</span>;
                                }
                              };
                              
                              return (
                                <div key={admin.profileId || admin.steamid} className="flex items-center justify-between p-3 bg-gmod-lighter rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-8 h-8">
                                      {getPrizeIcon(index)}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <img 
                                        src={admin.avatar || '/default-avatar.png'} 
                                        alt="Avatar"
                                        className="w-8 h-8 rounded-full"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                      <div>
                                        <a 
                                          href={`/players/${admin.profileId || resolveProfileLinkId(admin.steamid, admin.sid64)}`}
                                          className="text-theme-primary font-medium text-sm hover:underline"
                                        >
                                          {admin.displayName || admin.name || 'Неизвестно'}
                                        </a>
                                        <p className="text-gmod-white opacity-60 text-xs">
                                          {admin.rank} • {admin.onlineHours.toFixed(1)}ч
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-theme-primary font-bold text-lg">
                                      {admin.complaintsCount}
                                    </p>
                                    <p className="text-gmod-white opacity-60 text-xs">репортов</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer mt-8">
                <div className="px-6 py-4 border-b border-gmod-lighterer">
                  <h2 className="text-lg font-semibold text-gmod-white">Последняя активность</h2>
                </div>
                <div className="p-6">
                  {recentActivity.length === 0 ? (
                    <p className="text-gmod-white opacity-60 text-center py-8">Нет активности</p>
                  ) : (
                    <div className="space-y-4">
                      {recentActivity.map((activity) => (
                        <div key={activity.id || activity._id} className="flex items-center justify-between p-4 bg-gmod-lighter rounded-lg">
                          <div className="flex items-center space-x-4">
                            {getActionIcon(activity.type)}
                            <div>
                              <p className="text-gmod-white font-medium">
                                {activity.displayName || activity.executedBy?.displayName || activity.executedBy || 'Неизвестно'}
                                {activity.executedBy ? (
                                  <a
                                    href={`/players/${steamId64ToV2(activity.executedBy)}`}
                                    className="text-theme-primary font-semibold hover:underline ml-2"
                                  >
                                    {steamId64ToV2(activity.executedBy)}
                                  </a>
                                ) : null}
                              </p>
                              <p className="text-gmod-white opacity-60 text-sm">
                                {activity.type}
                                {activity.type === 'adminsay' && extractAdminSayMessage(activity.command) ? ` — ${extractAdminSayMessage(activity.command)}` : ''}
                                {activity.target ? ` ${activity.target}` : ''}
                              </p>
                              {activity.reason && (
                                <p className="text-gmod-white opacity-40 text-xs">Причина: {activity.reason}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center text-gmod-white opacity-60 text-sm">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatDate(activity.executedAt)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {activityTotalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gmod-lighterer">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => setActivityPage(Math.max(1, activityPage - 1))}
                        disabled={activityPage === 1}
                        className="px-3 py-1 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer text-sm"
                      >
                        Назад
                      </button>
                      <span className="px-3 py-1 text-gmod-white text-sm">
                        {activityPage} из {activityTotalPages}
                      </span>
                      <button
                        onClick={() => setActivityPage(Math.min(activityTotalPages, activityPage + 1))}
                        disabled={activityPage === activityTotalPages}
                        className="px-3 py-1 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer text-sm"
                      >
                        Вперед
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {tab === 'staff' && (
            <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer p-6">
              <h2 className="text-lg font-semibold text-gmod-white mb-4">Стафф</h2>
              <p className="text-gmod-white opacity-60">Информация о стаффе будет здесь.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Dashboard; 